import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEnrollments } from '../../hooks/useEnrollments';
import EnrollmentForm from '../../components/forms/EnrollmentForm';
import Card from '../../components/common/Card';

const EnrollmentEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { enrollments, updateEnrollment } = useEnrollments();

  // Read optional return hints (when opened from Student Details)
  const params = new URLSearchParams(location.search);
  const fromStudent = params.get('fromStudent'); // student id
  const intake = params.get('intake');           // intake id (optional)

  const [loading, setLoading] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // Centralized back nav (used by Cancel and after Save)
  const goBack = (replace = true) => {
    if (fromStudent) {
      navigate(
        `/students/${fromStudent}?tab=enrollments${intake ? `&intake=${intake}` : ''}`,
        { replace }
      );
    } else {
      // Fallback when opened from global list
      navigate('/enrollments', { replace });
    }
  };

  useEffect(() => {
    // Find the enrollment by ID; compare as strings to avoid number/string mismatch
    const foundEnrollment = enrollments.find(e => String(e.id) === String(id));
    if (foundEnrollment) {
      setEnrollment(foundEnrollment);
      setNotFound(false);
    } else if (enrollments.length > 0) {
      // If enrollments are loaded but not found
      setNotFound(true);
    }
  }, [id, enrollments]);

  const handleSubmit = async (formData) => {
    setLoading(true);
    try {
      const result = await updateEnrollment(id, formData);
      if (result.success) {
        alert('Enrollment updated successfully!');
        // go back to Student Details (if fromStudent present) or fall back to list
        goBack(true);
      } else {
        alert(result.error || 'Failed to update enrollment');
      }
    } catch (error) {
      alert('An unexpected error occurred');
      console.error('Error updating enrollment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    goBack(true); // replace history on cancel
  };

  if (notFound) {
    return (
      <div className="space-y-6">
        <h1 className="text-xl font-bold text-gray-900">Enrollment Not Found</h1>
        <Card>
          <div className="py-8 text-center">
            <p className="text-gray-500">The enrollment you are trying to edit does not exist.</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!enrollment) {
    return (
      <div className="space-y-6">
        <h1 className="text-xl font-bold text-gray-900">Edit Enrollment</h1>
        <Card>
          <div className="py-8 text-center">
            <p className="text-gray-500">Loading enrollment data...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-bold text-gray-900">Edit Enrollment</h1>
      <Card>
        <EnrollmentForm
          initialData={enrollment}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={loading}
        />
      </Card>
    </div>
  );
};

export default EnrollmentEdit;
